/**
 * ImageUploader — file picker + preview + WebP optimization for the
 * admin SPA's editor pages.
 *
 * The component is "controlled by blob, initialized by URL": it
 * accepts a `value` string (the existing image URL/path from the
 * server) and an `onChange` callback that delivers either a freshly-
 * picked `Blob` (so the parent can attach it to FormData on submit)
 * or the unchanged URL string (so the parent can submit without
 * touching the field).
 *
 * On file pick:
 *   1. Run optimizeImage() — converts to WebP, resizes to ≤ 2048px edge.
 *   2. Show a preview via URL.createObjectURL on the optimized blob.
 *   3. Call onChange(blob) so the parent can stash it for submission.
 *
 * The "remove" button calls onChange(null), letting the parent decide
 * what that means — for products/blog that's "leave as-is on submit"
 * (existing image kept), for category/brand editors (when added) it
 * could mean "set to null."
 */
import { useEffect, useRef, useState } from 'react'
import { ImagePlus, Loader2, X } from 'lucide-react'
import { toast } from 'sonner'
import { optimizeImage } from '../lib/imageOptimize'

export interface ImageUploaderProps {
  /** Current value — either an existing URL/path (string) or null. */
  value: string | null
  /** Called with a freshly-picked Blob, the unchanged original URL
   *  string (when the user picks a file that already matches), or
   *  null when the user clicks Remove. */
  onChange: (next: Blob | string | null) => void
  /** Form field name (e.g. `image`, `coverImage`, `logo`). Shown as
   *  the placeholder hint; not used for upload identification here. */
  label?: string
  /** Max file size in bytes (default 5 MB). */
  maxBytes?: number
  disabled?: boolean
}

export function ImageUploader({
  value,
  onChange,
  label = 'Image',
  maxBytes = 5 * 1024 * 1024,
  disabled = false,
}: ImageUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(value)
  const [busy, setBusy] = useState(false)

  // Sync the preview with the externally-controlled value, but only
  // when it's a URL/path string — blob previews are local to this
  // component and revoked on cleanup.
  useEffect(() => {
    if (typeof value === 'string') setPreviewUrl(value)
    else if (value === null) setPreviewUrl(null)
  }, [value])

  // Revoke any blob: URLs we created when the component unmounts.
  useEffect(() => {
    return () => {
      if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function handlePick(file: File) {
    if (file.size > maxBytes) {
      toast.error(`File is too large (max ${Math.round(maxBytes / 1024 / 1024)} MB).`)
      return
    }
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file.')
      return
    }

    setBusy(true)
    try {
      const optimized = await optimizeImage(file)
      const url = URL.createObjectURL(optimized)
      // Revoke the previous blob preview if any
      setPreviewUrl((prev) => {
        if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev)
        return url
      })
      onChange(optimized)
    } catch (err) {
      toast.error('Failed to process image', { description: (err as Error).message })
    } finally {
      setBusy(false)
    }
  }

  function handleRemove() {
    setPreviewUrl((prev) => {
      if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev)
      return null
    })
    onChange(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  return (
    <div className="space-y-2">
      <div className="flex items-start gap-3">
        <div className="relative w-32 h-32 rounded-md border border-line bg-ivory overflow-hidden flex items-center justify-center shrink-0">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={`${label} preview`}
              className="w-full h-full object-contain"
            />
          ) : (
            <ImagePlus className="size-6 text-slatey" aria-hidden />
          )}
          {busy && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
              <Loader2 className="size-5 text-forest animate-spin" />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            disabled={disabled || busy}
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) handlePick(f)
            }}
            className="text-xs text-slatey file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-line file:text-xs file:font-medium file:bg-white file:text-ink hover:file:bg-ivory file:cursor-pointer"
          />
          {previewUrl && (
            <button
              type="button"
              onClick={handleRemove}
              disabled={disabled || busy}
              className="inline-flex items-center gap-1.5 self-start text-xs text-danger hover:underline disabled:opacity-50"
            >
              <X className="size-3" /> Remove
            </button>
          )}
          <p className="text-[10px] text-slatey leading-relaxed">
            Auto-converted to WebP, resized to max 2048px via sharp on the server.
          </p>
        </div>
      </div>
    </div>
  )
}